import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";

import RangedDatePicker from "../../../../shared/Dates/RangedDatePicker";
import {
  usePaymentOverview,
  useTotalPaymentsPerDay,
  useWalletSummary,
} from "../../../../api/queries/payment/paymentQueries";
import { DashboardViewProps, DataPerWeek, PaymentOverview, TotalPaymentsPerDay } from "../../payment.types";

import PaymentAccount from "./PaymentAccount";
import PaymentOverviewCards from "./PaymentOverviewCards";
import TotalPayment, { ChartSkeleton } from "./TotalPayment";
import Transaction from "./Transaction";
import PaymentTable from "./PaymentTable/PaymentTable";
import NoPaymentsFigure from "./NoPaymentsFigure";

const isEffectivelyZero = (value: number | null | undefined): boolean => value === 0 || value == null;

const shouldRenderOverview = ({ data, isLoading }: { data?: PaymentOverview; isLoading: boolean }): boolean => {
  if (isLoading || !data) return false;

  return !Object.values(data).every(({ amount }) => isEffectivelyZero(amount));
};

const isZeroArray = (arr: DataPerWeek[]): boolean =>
  arr.every(
    ({ lastWeekValue, previousWeekValue }) => isEffectivelyZero(lastWeekValue) && isEffectivelyZero(previousWeekValue)
  );

const shouldRenderTotalPaymentsTransaction = ({ data }: { data?: TotalPaymentsPerDay }): boolean => {
  if (!data || !data.totalPayment || !data.transactions) return false;

  return !(isZeroArray(data.totalPayment) && isZeroArray(data.transactions));
};

const DashboardView = ({ user, handleAddReceivingDetails }: DashboardViewProps) => {
  const { t } = useTranslation();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const { userTotalPaymentsPerDay, isUserTotalPaymentsPerDayLoading, isUserTotalPaymentsPerDayHasError } =
    useTotalPaymentsPerDay({ dateRange });

  const { userPaymentOverview, isUserPaymentOverviewLoading, isUserPaymentOverviewHasError } = usePaymentOverview();
  const { walletSummary, isWalletSummaryLoading, isWalletSummaryHasError } = useWalletSummary();

  const shouldRenderOverviewSection = shouldRenderOverview({
    data: userPaymentOverview,
    isLoading: isUserPaymentOverviewLoading,
  });

  const shouldRenderPaymentsTransactionsSection = shouldRenderTotalPaymentsTransaction({
    data: userTotalPaymentsPerDay,
  });

  const IS_NO_PAYMENTS =
    (!shouldRenderOverviewSection &&
      !shouldRenderPaymentsTransactionsSection &&
      !isUserPaymentOverviewLoading &&
      !isUserTotalPaymentsPerDayLoading) ||
    isUserPaymentOverviewHasError;

  return (
    <>
      <PaymentAccount user={user} handleAddReceivingDetails={handleAddReceivingDetails} />
      <Box
        sx={{
          boxShadow: "none",
          borderRadius: "12px !important",
          border: "1px solid #EAECF0",
          background: "#FFF",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          rowGap: "16px",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "24px" }}>
          <Box>
            <Typography
              component="h5"
              sx={{
                color: "#1C1C1C",
                fontFamily: "Montserrat",
                fontSize: "20px",
                fontStyle: "normal",
                fontWeight: 700,
                lineHeight: "30px",
                marginBottom: "6px",
              }}
            >
              {t("pages.payment.overview.dashboard.paymentOverview")}
            </Typography>
            <Typography
              component="p"
              sx={{
                color: "var(--Dark---1000, #111)",
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "20px",
              }}
            >
              {t("pages.payment.overview.dashboard.overviewSubtitle")}
            </Typography>
          </Box>

          <RangedDatePicker dateRange={dateRange} setDateRange={setDateRange} />
        </Box>
        {IS_NO_PAYMENTS && <NoPaymentsFigure />}
        {!IS_NO_PAYMENTS && (
          <PaymentOverviewCards
            walletSummary={walletSummary}
            paymentOverviewData={userPaymentOverview}
            isLoading={isUserPaymentOverviewLoading || isWalletSummaryLoading}
          />
        )}
        {isUserTotalPaymentsPerDayLoading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              columnGap: "16px",
              rowGap: "32px",
            }}
          >
            <ChartSkeleton />
            <ChartSkeleton />
          </Box>
        )}
        {!IS_NO_PAYMENTS && !isUserTotalPaymentsPerDayLoading && userTotalPaymentsPerDay && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              columnGap: "16px",
              rowGap: "32px",
            }}
          >
            <TotalPayment
              weeklyData={userTotalPaymentsPerDay.totalPayment}
              lastWeek={userTotalPaymentsPerDay.lastWeek}
              previousWeek={userTotalPaymentsPerDay.previousWeek}
              total={userTotalPaymentsPerDay?.totalPaymentTotal}
            />
            <Transaction
              weeklyData={userTotalPaymentsPerDay.transactions}
              lastWeek={userTotalPaymentsPerDay.lastWeek}
              previousWeek={userTotalPaymentsPerDay.previousWeek}
              totalPaymentTotal={userTotalPaymentsPerDay?.totalPaymentTotal}
              transactionTotal={userTotalPaymentsPerDay?.transactionTotal}
            />
          </Box>
        )}
      </Box>
      <PaymentTable />
    </>
  );
};

export default DashboardView;
